/* =====================================================================
 * Workspace API Client — 워크스페이스 아이템/콘텐츠/파일/메시지 관리
 * ===================================================================== */

import type {
  WorkspaceItem,
  WorkspaceContent,
  WorkspaceFile,
  WorkspaceFileRef,
  WorkspaceMessage,
  WorkspaceStats,
  BrowserLiveViewPayload,
  WorkspaceMode,
  WorkspaceScope,
  WorkspaceKind,
} from '@auw/workspace-utils';
import { TAB_ID } from './workspaceSSE';
import { authFetch, getHeaders as baseGetHeaders, API_BASE_URL } from './authFetch';

const WS_PREFIX = `${API_BASE_URL}/api/v1/workspace`;
const deletedWorkspaceItemIds = new Set<string>();

function getHeaders(withJson = true): Record<string, string> {
  const h = baseGetHeaders(withJson);
  if (TAB_ID) h['X-Tab-ID'] = TAB_ID;
  return h;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Workspace Items                                                     */
/* ------------------------------------------------------------------ */

export interface CreateItemParams {
  item_id?: string;
  mode: string;
  kind?: WorkspaceKind;
  title: string;
  scope?: WorkspaceScope;
  team_id?: string | null;
  parent_id?: string | null;
  sort_order?: number;
  session_id?: string | null;
  settings?: Record<string, unknown>;
}

export async function createItem(params: CreateItemParams): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  const item = await handleResponse<WorkspaceItem>(res);
  deletedWorkspaceItemIds.delete(item.item_id);
  return item;
}

export interface ListItemsParams {
  mode?: string;
  scope?: string;
  team_id?: string;
  parent_id?: string | null;
  skip?: number;
  limit?: number;
}

export async function listItems(
  params: ListItemsParams = {},
): Promise<{ items: WorkspaceItem[]; count: number }> {
  const qs = new URLSearchParams();
  if (params.mode) qs.set('mode', params.mode);
  if (params.scope) qs.set('scope', params.scope);
  if (params.team_id) qs.set('team_id', params.team_id);
  if (params.parent_id !== undefined) {
    qs.set('parent_id', params.parent_id === null ? 'root' : params.parent_id);
  }
  if (params.skip) qs.set('skip', String(params.skip));
  if (params.limit) qs.set('limit', String(params.limit));

  const res = await authFetch(`${WS_PREFIX}/items?${qs.toString()}`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function getRecentItems(
  mode?: string,
  limit = 50,
): Promise<{ items: WorkspaceItem[]; count: number }> {
  const qs = new URLSearchParams();
  if (mode) qs.set('mode', mode);
  qs.set('limit', String(limit));

  const res = await authFetch(`${WS_PREFIX}/items/recent?${qs.toString()}`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function getItem(itemId: string): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}`, {
    headers: getHeaders(),
  });
  return handleResponse<WorkspaceItem>(res);
}

export interface UpdateItemParams {
  title?: string;
  parent_id?: string | null;
  sort_order?: number;
  scope?: string;
  team_id?: string | null;
  kind?: string;
  session_id?: string | null;
  settings?: Record<string, unknown>;
  status?: string;
}

export async function updateItem(
  itemId: string,
  params: UpdateItemParams,
): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceItem>(res);
}

export interface SuggestItemTitleParams {
  content: string;
}

export interface SuggestItemTitleResponse {
  title: string;
  source: 'llm' | 'fallback';
}

export async function suggestItemTitle(
  itemId: string,
  params: SuggestItemTitleParams,
): Promise<SuggestItemTitleResponse> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/title/suggest`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<SuggestItemTitleResponse>(res);
}

export interface UpdateSharedGroupSettingsParams {
  title?: string;
  pinned?: boolean;
}

export async function updateSharedGroupSettings(
  itemId: string,
  params: UpdateSharedGroupSettingsParams,
): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/group/settings`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceItem>(res);
}

export async function leaveSharedGroup(
  itemId: string,
): Promise<{ left: boolean; item_id: string }> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/group/leave`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export interface InviteSharedGroupMembersParams {
  member_ids: string[];
}

export async function inviteSharedGroupMembers(
  itemId: string,
  params: InviteSharedGroupMembersParams,
): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/group/members`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceItem>(res);
}

export async function deleteSharedGroup(
  itemId: string,
): Promise<{ deleted: boolean; item_id: string }> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/group`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 404) {
    deletedWorkspaceItemIds.add(itemId);
    return { deleted: true, item_id: itemId };
  }
  const result = await handleResponse<{ deleted: boolean; item_id: string }>(res);
  deletedWorkspaceItemIds.add(result.item_id || itemId);
  return result;
}

export async function deleteItem(
  itemId: string,
  cascade = false,
): Promise<{ deleted: boolean; item_id: string }> {
  const qs = cascade ? '?cascade=true' : '';
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}${qs}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 404) {
    deletedWorkspaceItemIds.add(itemId);
    return { deleted: true, item_id: itemId };
  }
  const result = await handleResponse<{ deleted: boolean; item_id: string }>(res);
  deletedWorkspaceItemIds.add(result.item_id || itemId);
  return result;
}

export interface MoveItemParams {
  parent_id: string | null;
  sort_order?: number;
  scope?: string;
  team_id?: string | null;
}

export async function moveItem(
  itemId: string,
  params: MoveItemParams,
): Promise<WorkspaceItem> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/move`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceItem>(res);
}

export interface CreateShareLinkParams {
  public: boolean;
  password?: string;
}

export interface ShareLinkResponse {
  share_id: string;
  share_token: string;
  is_public: boolean;
  has_password: boolean;
  password: string | null;
  url_path: string;
}

export async function getShareLink(
  itemId: string,
): Promise<ShareLinkResponse> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/share-links`, {
    headers: getHeaders(),
  });
  return handleResponse<ShareLinkResponse>(res);
}

export async function createShareLink(
  itemId: string,
  params: CreateShareLinkParams,
): Promise<ShareLinkResponse> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/share-links`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<ShareLinkResponse>(res);
}

export interface SharedWorkspaceAccessResponse {
  item: WorkspaceItem;
  content: WorkspaceContent | null;
  messages: WorkspaceMessage[];
}

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

function getSharedWorkspaceAccessBases(): string[] {
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const configuredBase = API_BASE_URL.trim().replace(/\/$/, '');
  const bases: string[] = [];

  const pushBase = (value: string) => {
    const normalized = value.replace(/\/$/, '');
    if (!bases.includes(normalized)) bases.push(normalized);
  };

  if (typeof window !== 'undefined') {
    pushBase('');
  }

  if (configuredBase) {
    try {
      const parsed = new URL(configuredBase);
      if (!(bases.includes('') && isLocalHost(parsed.hostname) && isLocalHost(currentHost))) {
        pushBase(configuredBase);
      }
    } catch {
      pushBase(configuredBase);
    }
  }

  if (bases.length === 0) bases.push('');
  return bases;
}

export async function accessSharedWorkspace(
  shareToken: string,
  password?: string,
): Promise<SharedWorkspaceAccessResponse> {
  const body = JSON.stringify(password ? { password } : {});
  let networkError: Error | null = null;
  let responseError: Error | null = null;
  const bases = getSharedWorkspaceAccessBases();

  for (let index = 0; index < bases.length; index += 1) {
    const base = bases[index];
    const url = base
      ? `${base}/api/v1/workspace/shared/${encodeURIComponent(shareToken)}/access`
      : `/api/v1/workspace/shared/${encodeURIComponent(shareToken)}/access`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (!res.ok) {
        const text = await res.text();
        const error = new Error(`HTTP ${res.status}: ${text}`);
        if ((res.status === 404 || res.status >= 500) && index < bases.length - 1) {
          responseError = error;
          continue;
        }
        throw error;
      }
      return res.json();
    } catch (error) {
      if (error instanceof TypeError) {
        networkError = error;
        continue;
      }
      throw error;
    }
  }

  throw responseError ?? networkError ?? new Error('공유 데이터를 불러오지 못했습니다.');
}

/* ------------------------------------------------------------------ */
/* Workspace Contents                                                  */
/* ------------------------------------------------------------------ */

export async function getContent(itemId: string): Promise<WorkspaceContent> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/content`, {
    headers: getHeaders(),
  });
  return handleResponse<WorkspaceContent>(res);
}

export interface SaveContentParams {
  content_type: string;
  content: unknown;
  expected_version?: number;
}

export async function saveContent(
  itemId: string,
  params: SaveContentParams,
): Promise<WorkspaceContent> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/content`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceContent>(res);
}

/* ------------------------------------------------------------------ */
/* Workspace Files                                                     */
/* ------------------------------------------------------------------ */

export interface UploadFilesResult {
  uploaded: WorkspaceFile[];
  count: number;
  errors?: Array<{ file: string; error: string }> | null;
}

export async function uploadWorkspaceFiles(
  files: File[],
  itemId?: string,
  refType = 'attachment',
): Promise<UploadFilesResult> {
  const form = new FormData();
  if (itemId) form.append('item_id', itemId);
  form.append('ref_type', refType);
  for (const f of files) {
    form.append('files', f);
  }

  const res = await authFetch(`${WS_PREFIX}/files/upload`, {
    method: 'POST',
    headers: getHeaders(false),
    body: form,
  });
  return handleResponse<UploadFilesResult>(res);
}

export async function listFiles(
  params: {
    session_id?: string;
    item_id?: string;
    source_type?: string;
    mime_type?: string;
    scope?: 'personal' | 'team';
    team_id?: string;
    skip?: number;
    limit?: number;
  } = {},
): Promise<{ files: WorkspaceFile[]; count: number }> {
  const qs = new URLSearchParams();
  if (params.session_id) qs.set('session_id', params.session_id);
  if (params.item_id) qs.set('item_id', params.item_id);
  if (params.source_type) qs.set('source_type', params.source_type);
  if (params.mime_type) qs.set('mime_type', params.mime_type);
  if (params.scope) qs.set('scope', params.scope);
  if (params.team_id) qs.set('team_id', params.team_id);
  if (params.skip) qs.set('skip', String(params.skip));
  if (params.limit) qs.set('limit', String(params.limit));

  const res = await authFetch(`${WS_PREFIX}/files?${qs.toString()}`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export async function getFile(fileId: string): Promise<WorkspaceFile> {
  const res = await authFetch(`${WS_PREFIX}/files/${fileId}`, {
    headers: getHeaders(),
  });
  return handleResponse<WorkspaceFile>(res);
}

export function getFileDownloadUrl(fileId: string): string {
  return `${WS_PREFIX}/files/${fileId}/download`;
}

/**
 * Build the canonical ``/workspace/files/{file_id}/serve`` URL.
 *
 * `version` 은 in-place revision (자재관리 에이전트가 ``replace_storage`` 로
 * 같은 ``file_id`` 의 새 blob 으로 swap 한 케이스) 후 브라우저 HTTP 캐시를
 * 회피하기 위한 cache buster. URL path 가 같으면 브라우저가 304/디스크
 * 캐시 응답을 반환하므로, AI 가 새 bytes 를 저장해도 ``<img>`` /
 * ``fetch(url)`` 가 옛 응답을 보여주는 회귀가 발생한다.
 *
 * 호출부 권장 값 (있는 만큼 fall through):
 *   1. ``file.metadata.revision_index`` — 가장 결정적 (백엔드 ``replace_storage``
 *      가 revision 마다 +1)
 *   2. ``file.updated_at`` — revision_index 미노출 케이스의 fallback
 *   3. ``file.size_bytes`` — 마지막 fallback (편집 후 byte size 가 변하는
 *      케이스에서 약하지만 동작)
 *
 * @param fileId workspace_files.file_id (UUID)
 * @param version cache buster — number | string | null/undefined (생략 가능)
 */
export function getFileServeUrl(
  fileId: string,
  version?: number | string | null,
): string {
  const base = `${WS_PREFIX}/files/${fileId}/serve`;
  if (version === null || version === undefined || version === '') return base;
  return `${base}?v=${encodeURIComponent(String(version))}`;
}

export function getFileThumbnailUrl(
  fileId: string,
  version?: number | string | null,
): string {
  const base = `${WS_PREFIX}/files/${fileId}/thumbnail`;
  if (version === null || version === undefined || version === '') return base;
  return `${base}?v=${encodeURIComponent(String(version))}`;
}

export interface SignedUrlResponse {
  url: string | null;
  source: 'cloudfront' | 'not_on_s3' | 'unavailable';
  expires_in?: number;
}

export async function getFileSignedUrl(
  fileId: string,
  mode: 'download' | 'serve' | 'thumbnail' = 'download',
): Promise<SignedUrlResponse> {
  try {
    const res = await authFetch(
      `${WS_PREFIX}/files/${fileId}/signed-url?mode=${mode}`,
      { headers: baseGetHeaders() },
    );
    if (!res.ok) return { url: null, source: 'unavailable' };
    return await res.json();
  } catch {
    return { url: null, source: 'unavailable' };
  }
}

export async function shareFile(
  fileId: string,
  targetItemId: string,
  refType = 'attachment',
): Promise<WorkspaceFileRef> {
  const res = await authFetch(`${WS_PREFIX}/files/${fileId}/share`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ target_item_id: targetItemId, ref_type: refType }),
  });
  return handleResponse<WorkspaceFileRef>(res);
}

export async function deleteFile(
  fileId: string,
): Promise<{ deleted: boolean; file_id: string }> {
  const res = await authFetch(`${WS_PREFIX}/files/${fileId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export interface UpdateFileParams {
  original_name?: string;
}

export async function updateFile(
  fileId: string,
  params: UpdateFileParams,
): Promise<WorkspaceFile> {
  const res = await authFetch(`${WS_PREFIX}/files/${fileId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceFile>(res);
}

export async function getFileCounts(
  itemIds: string[],
): Promise<Record<string, number>> {
  if (itemIds.length === 0) return {};
  const res = await authFetch(`${WS_PREFIX}/files/counts`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ item_ids: itemIds }),
  });
  const data = await handleResponse<{ counts: Record<string, number> }>(res);
  return data.counts;
}

/* ------------------------------------------------------------------ */
/* Workspace Messages                                                  */
/* ------------------------------------------------------------------ */

export async function listMessages(
  itemId: string,
  params: { skip?: number; limit?: number } = {},
): Promise<{ messages: WorkspaceMessage[]; count: number }> {
  if (deletedWorkspaceItemIds.has(itemId)) {
    return { messages: [], count: 0 };
  }

  const qs = new URLSearchParams();
  if (params.skip) qs.set('skip', String(params.skip));
  if (params.limit) qs.set('limit', String(params.limit));

  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/messages?${qs.toString()}`, {
    headers: getHeaders(),
  });
  if (res.status === 404) {
    deletedWorkspaceItemIds.add(itemId);
    return { messages: [], count: 0 };
  }
  return handleResponse(res);
}

export interface CreateMessageParams {
  role: string;
  content: string;
  content_type?: string;
  attachments?: string[];
  metadata?: Record<string, unknown>;
}

export async function createMessage(
  itemId: string,
  params: CreateMessageParams,
): Promise<WorkspaceMessage> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<WorkspaceMessage>(res);
}

export async function updateMessageMetadata(
  itemId: string,
  messageId: string,
  metadata: Record<string, unknown>,
): Promise<WorkspaceMessage> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/messages/${messageId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ metadata }),
  });
  return handleResponse<WorkspaceMessage>(res);
}

export async function deleteMessage(
  itemId: string,
  messageId: string,
): Promise<{ deleted: boolean; message_id: string }> {
  const res = await authFetch(`${WS_PREFIX}/items/${itemId}/messages/${messageId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(res);
}

export interface OpenWorkspaceLiveViewParams {
  live_view_ref: string;
  item_id?: string | null;
}

export interface OpenWorkspaceLiveViewResponse {
  live_view_ref: string;
  viewer_url: string;
  viewer_expires_at: string;
  watch_only: boolean;
}

export async function openWorkspaceLiveView(
  workspaceType: WorkspaceMode | 'ppt',
  params: OpenWorkspaceLiveViewParams,
): Promise<OpenWorkspaceLiveViewResponse> {
  const res = await authFetch(`${API_BASE_URL}/api/v1/workspaces/${workspaceType}/live-view/open`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse<OpenWorkspaceLiveViewResponse>(res);
}

export async function getWorkspaceLiveViewStatus(
  workspaceType: WorkspaceMode | 'ppt',
  params: {
    live_view_ref: string;
    item_id?: string | null;
  },
): Promise<{ live_view: BrowserLiveViewPayload }> {
  const qs = new URLSearchParams();
  qs.set('live_view_ref', params.live_view_ref);
  if (params.item_id) qs.set('item_id', params.item_id);
  const res = await authFetch(`${API_BASE_URL}/api/v1/workspaces/${workspaceType}/live-view/status?${qs.toString()}`, {
    headers: getHeaders(),
  });
  return handleResponse<{ live_view: BrowserLiveViewPayload }>(res);
}

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

export async function getWorkspaceStats(): Promise<WorkspaceStats> {
  const res = await authFetch(`${WS_PREFIX}/stats`, {
    headers: getHeaders(),
  });
  return handleResponse<WorkspaceStats>(res);
}
